import { getActivityIcon, getActivityTypeColor } from '../../utils/activityIcons';

/**
 * Activity Card Component
 * Simple list item representation of an activity with drag handle and actions
 */

const ActivityCard = ({
  activity,
  index,
  onEdit,
  onDelete,
  onDuplicate,
  onPreview,
  subsessions,
  onAssignToSubsession,
  isDragging = false,
  dragHandleProps,
}) => {
  return (
    <div
      className={`group flex items-center gap-3 px-3 py-2 bg-white border rounded-lg transition-all ${
        isDragging
          ? 'border-primary-500 shadow-lg opacity-75'
          : 'border-neutral-200 hover:border-neutral-300'
      }`}
    >
      {/* Drag Handle */}
      <div
        {...dragHandleProps}
        className="flex-shrink-0 cursor-grab active:cursor-grabbing text-neutral-400 hover:text-neutral-600"
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M8 6a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM8 12a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM8 18a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM14 6a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM14 12a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM14 18a2 2 0 1 1-4 0 2 2 0 0 1 4 0z" />
        </svg>
      </div>

      {/* Order Number */}
      <div className="flex-shrink-0 w-6 h-6 rounded bg-neutral-100 flex items-center justify-center text-xs font-semibold text-neutral-600">
        {index + 1}
      </div>

      {/* Icon */}
      <div className={`flex-shrink-0 w-8 h-8 rounded flex items-center justify-center ${getActivityTypeColor(activity.type)}`}>
        {getActivityIcon(activity.type, 'sm')}
      </div>

      {/* Activity Info */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-neutral-900 truncate">
          {activity.title}
        </p>
        <p className="text-xs text-neutral-500 capitalize">
          {activity.type?.replace('_', ' ')} {activity.timerEnabled && activity.duration ? `• ${activity.duration} Min` : ''}
        </p>
      </div>

      {/* Action Buttons - always visible on mobile, hover on desktop */}
      <div className="flex-shrink-0 flex items-center gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
        <button
          onClick={() => onPreview(activity)}
          className="p-1.5 text-neutral-400 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"
          title="Preview"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
        </button>

        <button
          onClick={() => onEdit(activity)}
          className="p-1.5 text-neutral-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
          title="Edit"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        </button>

        <button
          onClick={() => onDuplicate(activity)}
          className="p-1.5 text-neutral-400 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
          title="Duplicate"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        </button>

        <button
          onClick={() => onDelete(activity)}
          className="p-1.5 text-neutral-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
          title="Delete"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default ActivityCard;
